/**
 * <product-ugc-carousel>
 * "As worn by you" UGC row on the product page (Figma node 1:1533).
 *
 * The row is a native horizontal scroller, so it stays swipeable/draggable and
 * keyboard scrollable. On top of that it is nudged along by a requestAnimationFrame
 * loop at `data-speed` pixels per second (node 1:1540 annotation: "auto-scroll really
 * slowly", merchant controlled). The cards run once: the row travels to its last card
 * and stops there rather than wrapping back around.
 *
 * Auto-scroll freezes while a card is hovered or focused, while the row is off-screen,
 * once it has reached the end, and for reduced-motion visitors. Manual scrolling always
 * wins: the loop re-syncs to wherever the visitor left the row, and scrolling back from
 * the end gives it somewhere to travel again.
 *
 * The header indicator behaves like a scrollbar — the thumb is sized to the visible
 * fraction of the row and travels as the row scrolls.
 *
 * Markup contract:
 *   <product-ugc-carousel data-autoplay="true" data-speed="25">
 *     <... data-progress><span data-progress-thumb></span></...>
 *     <div data-viewport>
 *       <ul data-track>...cards...</ul>
 */
if (!customElements.get('product-ugc-carousel')) {
  class ProductUgcCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-viewport]');
      this.track = this.querySelector('[data-track]');
      this.progress = this.querySelector('[data-progress]');
      this.thumb = this.querySelector('[data-progress-thumb]');

      if (!this.viewport || !this.track || this.track.children.length === 0) return;

      this.position = 0;
      this.applied = 0;
      this.lastTime = 0;
      this.frame = null;
      this.hovered = false;
      this.focused = false;
      this.onScreen = false;
      this.speed = Math.max(1, parseFloat(this.dataset.speed) || 25);

      this.onTick = this.onTick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onResize = this.onResize.bind(this);
      this.setup = this.setup.bind(this);

      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reduceMotion.addEventListener('change', this.setup);

      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport.addEventListener('pointerenter', this.onPointerEnter);
      this.viewport.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);

      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this);

      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.onScreen = entries.some((entry) => entry.isIntersecting);
        this.sync();
      });
      this.intersectionObserver.observe(this);

      this.setup();
    }

    disconnectedCallback() {
      this.stop();
      this.reduceMotion?.removeEventListener('change', this.setup);
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.viewport?.removeEventListener('pointerenter', this.onPointerEnter);
      this.viewport?.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      clearTimeout(this.resizeTimer);
    }

    get autoplayAllowed() {
      return this.dataset.autoplay !== 'false' && !this.reduceMotion.matches;
    }

    get shouldPlay() {
      return this.autoScrolls && this.onScreen && !this.hovered && !this.focused && !this.atEnd;
    }

    /** The last card is in frame, so there is nothing left to travel. */
    get atEnd() {
      return this.viewport.scrollLeft >= this.overflow - 1;
    }

    setup() {
      this.stop();
      this.measure();
      this.position = this.clamp(this.viewport.scrollLeft);
      this.viewport.scrollLeft = this.position;
      this.applied = this.viewport.scrollLeft;
      this.updateIndicator();
      this.sync();
    }

    /**
     * The row is a plain scroller, so its travel is however far the cards overflow the
     * viewport. Auto-scroll only has somewhere to go while that is positive.
     */
    measure() {
      this.contentWidth = this.track.scrollWidth;
      this.overflow = Math.max(0, this.contentWidth - this.viewport.clientWidth);
      this.autoScrolls = this.autoplayAllowed && this.overflow > 0;
    }

    start() {
      if (this.frame !== null) return;
      this.lastTime = 0;
      this.position = this.viewport.scrollLeft;
      this.frame = requestAnimationFrame(this.onTick);
    }

    stop() {
      if (!this.frame) return;
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    sync() {
      if (this.shouldPlay) {
        this.start();
      } else {
        this.stop();
      }
    }

    onTick(time) {
      // Clamped so a backgrounded tab doesn't resume with a jump.
      const elapsed = this.lastTime ? Math.min(64, time - this.lastTime) : 0;
      this.lastTime = time;

      // Tracked as a float: at slow speeds the per-frame delta is sub-pixel, and
      // reading scrollLeft back each frame would round it away.
      this.position = this.clamp(this.position + (this.speed * elapsed) / 1000);
      this.viewport.scrollLeft = this.position;
      this.applied = this.viewport.scrollLeft;
      this.updateIndicator();

      // Parked on the last card. Dropped rather than left spinning on the spot; only
      // scrolling back gives it somewhere to travel again.
      if (this.position >= this.overflow) {
        this.frame = null;
        return;
      }

      this.frame = requestAnimationFrame(this.onTick);
    }

    /** The row runs to its ends and stops there rather than wrapping around. */
    clamp(offset) {
      return Math.min(Math.max(offset, 0), this.overflow);
    }

    onScroll() {
      // Only react to scrolling the visitor caused; our own writes are already applied.
      if (Math.abs(this.viewport.scrollLeft - this.applied) > 1) {
        this.position = this.viewport.scrollLeft;
        // Picks the travel back up when the visitor pulls the row away from the end.
        this.sync();
      }

      if (this.frame === null) this.updateIndicator();
    }

    onPointerEnter() {
      this.hovered = true;
      this.sync();
    }

    onPointerLeave() {
      this.hovered = false;
      this.sync();
    }

    onFocusIn() {
      this.focused = true;
      this.sync();
    }

    onFocusOut() {
      this.focused = false;
      this.sync();
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(this.setup, 150);
    }

    /**
     * Scrollbar-style indicator: thumb width mirrors the visible fraction of the
     * row, and its offset mirrors how far through the cards the row has travelled.
     */
    updateIndicator() {
      if (!this.progress || !this.thumb) return;

      const fraction = this.contentWidth > 0 ? Math.min(1, this.viewport.clientWidth / this.contentWidth) : 1;

      this.progress.hidden = fraction >= 1;
      if (this.progress.hidden) return;

      const ratio = this.overflow > 0 ? Math.min(Math.max(this.viewport.scrollLeft / this.overflow, 0), 1) : 0;
      const offset = this.progress.clientWidth * (1 - fraction) * ratio;

      this.thumb.style.setProperty('--product-ugc-thumb-width', `${fraction * 100}%`);
      this.thumb.style.setProperty('--product-ugc-thumb-offset', `${offset}px`);
    }
  }

  customElements.define('product-ugc-carousel', ProductUgcCarousel);
}
